import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Form, Button, Badge, InputGroup } from "react-bootstrap";
import useApi from "../../Hooks/useApi";
import { getListingItems } from "../../api/getapi";
import axios from "axios";
import ImageUploader from "./ImageUpoader";
import * as postapi from "../../api/postapi";
import { FiSearch, FiX, FiTrash, FiLink, FiCalendar, FiPercent, FiImage, FiTag, FiSettings, FiPackage, FiCheckCircle, FiPlusCircle, FiEdit2, FiArrowLeft } from "react-icons/fi";
import "./SalesAndDealsForm.css";

function SalesAndDealsForm({ editingDeal, onFormSubmit, setShowForm }) {
  const [dealName, setDealName] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [errors, setErrors] = useState({});
  const [banners, setBanners] = useState([]);
  const [colorGradient, setColorGradient] = useState("");
  const [imageLinks, setImageLinks] = useState({ files: [] });
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);

  // Search states (debounced dropdown)
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchIdQuery, setSearchIdQuery] = useState("");
  const [searchIdResults, setSearchIdResults] = useState([]);
  const [isSearchingId, setIsSearchingId] = useState(false);
  const [showIdDropdown, setShowIdDropdown] = useState(false);
  // Separate state for existing banners in edit mode
  const [existingBanners, setExistingBanners] = useState([]);

  const imageUploaderRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchIdTimeoutRef = useRef(null);
  const dropdownIdRef = useRef(null);

  const AddSale = useApi(postapi.AddSale, { hasCatchError: true });
  const UpdateSale = useApi(postapi.UpdateSale, { hasCatchError: true });
  const { request: getItems } = useApi(getListingItems, { hasCatchError: true });

  const colorPalette = [
    { name: "Sunset", colors: ["#ff9a9e", "#fecfef"] },
    { name: "Ocean", colors: ["#667eea", "#764ba2"] },
    { name: "Forest", colors: ["#a8e063", "#56ab2f"] },
    { name: "Fire", colors: ["#ff416c", "#ff4b2b"] },
    { name: "Purple", colors: ["#9d50bb", "#6e0dd0"] },
    { name: "Blue Sky", colors: ["#74b9ff", "#0984e3"] },
    { name: "Green Nature", colors: ["#00b894", "#00a085"] },
    { name: "Orange Burst", colors: ["#fdcb6e", "#e17055"] },
    { name: "Pink Dream", colors: ["#fd79a8", "#e84393"] },
    { name: "Teal Wave", colors: ["#00cec9", "#00b894"] },
    { name: "Red Passion", colors: ["#e17055", "#d63031"] },
    { name: "Golden Hour", colors: ["#fdcb6e", "#f39c12"] },
    { name: "Mint Fresh", colors: ["#74b9ff", "#00cec9"] },
    { name: "Lavender", colors: ["#a29bfe", "#6c5ce7"] },
    { name: "Coral Reef", colors: ["#ff7675", "#fd79a8"] },
    { name: "Electric", colors: ["#00cec9", "#74b9ff"] },
  ];

  // Add this function after your other handler functions
  // Function to generate linear gradient from color palette
  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const handleDealNameChange = (e) => {
    const value = e.target.value.trimEnd().replace(/\s+/g, "-");
    setDealName(value);
    if (!slugEdited) {
      setSlug(generateSlug(value));
    }
  };

  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setSlug(value);
    setSlugEdited(true);
  };

  const handleColorPaletteSelect = (colors) => {
    const gradient = `linear-gradient(135deg, ${colors[0]}, ${colors[1]})`;
    setColorGradient(gradient);
  };
  useEffect(() => {
    if (editingDeal) {
      setDealName(editingDeal.name || "");
      setDiscountPercentage(editingDeal.discount_upto || "");
      setSelectedItems(editingDeal.listings || []);
      // Format dates correctly
      const formatLocalDate = (dateStr) => {
        const d = new Date(dateStr);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
      setStartDate(editingDeal.start_date ? formatLocalDate(editingDeal.start_date) : "");
      setEndDate(editingDeal.end_date ? formatLocalDate(editingDeal.end_date) : "");
      setColorGradient(editingDeal.color_scheme || "");
      setMetaTitle(editingDeal.meta_title || "");
      setMetaDescription(editingDeal.meta_description || "");
      setMetaKeywords(editingDeal.meta_keywords || "");
      setExistingBanners(editingDeal.banners || []);
      setSlug(editingDeal.slug || "");
      setSlugEdited(true);
    }
  }, [editingDeal]);

  // Debounced search by name
  const searchItems = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    try {
      const response = await getItems({ filter: query });
      const options = response.data.data.map((item) => ({
        value: item.id,
        label: item.title,
        image: item.image,
        price: item.effective_price,
        category: item.category,
      }));
      setSearchResults(options);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search by ID
  const searchItemsById = async (query) => {
    if (!query.trim() || !/^\d+$/.test(query.trim())) {
      setSearchIdResults([]);
      setIsSearchingId(false);
      return;
    }
    setIsSearchingId(true);
    try {
      const response = await getItems({ listingId: query });
      const options = response.data.data.map((item) => ({
        value: item.id,
        label: item.title,
        image: item.image,
        price: item.effective_price,
        category: item.category,
      }));
      setSearchIdResults(options);
    } catch (error) {
      setSearchIdResults([]);
    } finally {
      setIsSearchingId(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowDropdown(true);
    if (query.trim()) {
      setSearchIdQuery("");
      setSearchIdResults([]);
      setShowIdDropdown(false);
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchItems(query), 300);
  };

  const handleSearchIdChange = (e) => {
    const query = e.target.value;
    setSearchIdQuery(query);
    setShowIdDropdown(true);
    if (query.trim()) {
      setSearchQuery("");
      setSearchResults([]);
      setShowDropdown(false);
    }
    if (searchIdTimeoutRef.current) clearTimeout(searchIdTimeoutRef.current);
    searchIdTimeoutRef.current = setTimeout(() => searchItemsById(query), 300);
  };

  const handleSelectItem = (item) => {
    if (!selectedItems.find((i) => i.value === item.value)) {
      setSelectedItems([...selectedItems, item]);
    }
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
    setSearchIdQuery("");
    setSearchIdResults([]);
    setShowIdDropdown(false);
  };

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
      if (dropdownIdRef.current && !dropdownIdRef.current.contains(event.target)) setShowIdDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (searchIdTimeoutRef.current) clearTimeout(searchIdTimeoutRef.current);
    };
  }, []);

  const handleRemoveItem = (itemToRemove) => {
    setSelectedItems(selectedItems.filter((item) => item.value !== itemToRemove.value));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!dealName) newErrors.dealName = "Deal Name is required";
    if (!discountPercentage) newErrors.discountPercentage = "Discount Percentage is required";
    if (!selectedItems.length) newErrors.selectedItems = "At least one item must be selected";
    if (!startDate) newErrors.startDate = "Start Date is required";
    if (!endDate) newErrors.endDate = "End Date is required";
    else if (new Date(startDate) > new Date(endDate)) newErrors.endDate = "End Date must be after Start Date";

    // Check total banners (existing + new)
    const totalBanners = existingBanners.length + banners.length;
    if (totalBanners === 0) newErrors.banners = "At least one banner image is required";

    if (!colorGradient) newErrors.colorGradient = "Color Gradient is required";
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const UploadImage = async () => {
    if (!banners || banners.length === 0) {
      return; // Don't throw error, just return if no new banners to upload
    }

    const formData = new FormData();

    banners.forEach((file) => {
      if (!file?.data) {
        throw new Error("Invalid file data.");
      }
      formData.append("sales", file.data);
    });

    try {
      const response = await axios.post(`${process.env.REACT_APP_URL_IMAGE_CDN}/marketplace/banners`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setImageLinks({ files: response.data.files });
    } catch (error) {
      console.error("Error uploading image:", error?.response?.data || error.message);
    }
  };

  // Delete existing banner (for edit mode)
  const onDeleteExistingBanner = async (bannerToDelete) => {
    // Remove from existing banners state
    const updatedExistingBanners = existingBanners.filter((banner) => banner.banner_key !== bannerToDelete.banner_key);
    setExistingBanners(updatedExistingBanners);

    // Delete from server
    const parts = bannerToDelete.banner_key.split("/");
    const filenameWithExt = parts[parts.length - 1];
    const filenameWithoutExt = filenameWithExt.split(".")[0];

    try {
      await axios.delete(`${process.env.REACT_APP_URL_IMAGE_CDN}/marketplace/banners/${filenameWithoutExt}`, {
        withCredentials: true,
      });
    } catch (error) {
      console.error("Error deleting existing banner:", error?.response?.data || error.message);
    }
  };

  // Delete newly uploaded banner
  const onDeleteNewBanner = async (bannerToDelete) => {
    // Remove from imageLinks state
    const updatedFiles = imageLinks.files.filter((image) => image !== bannerToDelete);
    setImageLinks({ ...imageLinks, files: updatedFiles });

    // Delete from server
    const parts = bannerToDelete.split("/");
    const filenameWithExt = parts[parts.length - 1];
    const filenameWithoutExt = filenameWithExt.split(".")[0];

    try {
      await axios.delete(`${process.env.REACT_APP_URL_IMAGE_CDN}/marketplace/banners/${filenameWithoutExt}`, {
        withCredentials: true,
      });
    } catch (error) {
      console.error("Error deleting new banner:", error?.response?.data || error.message);
    }
  };

  // Only upload when new banners are added
  useEffect(() => {
    if (banners.length > 0) {
      UploadImage();
    }
  }, [banners]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      // Combine existing banners and new banners for the payload
      const allBanners = [
        // Existing banners (include id so backend can update instead of duplicate)
        ...existingBanners.map((banner) => ({
          id: banner.banner_id,
          location: banner.location || "main-header",
          banner_key: banner.banner_key,
        })),
        // New banners
        ...imageLinks.files.map((banner) => ({
          location: "main-header",
          banner_key: banner,
        })),
      ];

      const payload = {
        name: dealName,
        slug: slug,
        discount_upto: discountPercentage,
        listings: selectedItems.map((item) => ({
          value: item.value,
          label: item.label,
        })),
        start_date: startDate,
        end_date: endDate,
        color_scheme: colorGradient,
        banners: allBanners,
        meta_title: metaTitle,
        meta_description: metaDescription,
        meta_keywords: metaKeywords,
      };

      try {
        if (editingDeal) {
          await UpdateSale.request(editingDeal.sale_id, payload);
        } else {
          await AddSale.request(payload);
        }
        onFormSubmit();
      } catch (error) {
        console.log(error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Get total banner count for display
  const totalBannerCount = banners.length + existingBanners.length;

  const renderCardTitle = (icon, title, badge) => (
    <h6 className="sale-card-title">
      <span className="sale-card-icon">{icon}</span>
      {title}
      {badge != null && badge > 0 && <Badge className="sale-card-badge" pill>{badge}</Badge>}
    </h6>
  );

  const renderSearchDropdown = (results, isLoading, show, query, onSelect) => {
    if (!show) return null;
    return (
      <div className="sale-search-dropdown">
        {isLoading && <div className="sale-search-loading"><span className="spinner-border spinner-border-sm" /> Searching...</div>}
        {!isLoading && results.length > 0 && results.map((item) => {
          const alreadySelected = selectedItems.find((i) => i.value === item.value);
          return (
            <div key={item.value} className={`sale-search-result-item ${alreadySelected ? "disabled" : ""}`} onClick={() => !alreadySelected && onSelect(item)}>
              {item.image && <img src={`${process.env.REACT_APP_URL_S3}/${item.image}`} alt={item.label} className="sale-search-result-img" />}
              <div className="sale-search-result-info">
                <div className="sale-search-result-title">{item.label}</div>
                <div className="sale-search-result-meta"><small>ID: {item.value}</small>{item.price && <small>Rs. {Number(item.price).toLocaleString()}</small>}</div>
              </div>
              {alreadySelected && <Badge bg="secondary" pill>Added</Badge>}
            </div>
          );
        })}
        {!isLoading && query && results.length === 0 && <div className="sale-search-empty">No items found</div>}
      </div>
    );
  };

  return (
    <>
      <button type="button" className="sale-back-link" onClick={() => setShowForm(false)}>
        <FiArrowLeft aria-hidden="true" /> Back to Sales & Deals
      </button>
      <div className="sale-form-header">
        <div className="sale-form-identity">
          <span className="sale-form-icon">
            {editingDeal ? <FiEdit2 /> : <FiPlusCircle />}
          </span>
          <h1 className="sale-form-title">{editingDeal ? "Edit Deal" : "Create New Deal"}</h1>
          <span className="sale-form-subtitle">{editingDeal ? "Update promotional campaign details" : "Set up a new promotional campaign for your marketplace"}</span>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
      {/* Section 1: Basics */}
      <Card className="border-0 shadow-sm mb-3 sale-card">
        <Card.Body className="p-4">
          {renderCardTitle(<FiSettings />, "Basics")}
          <Row className="g-3">
            <Col md={6}><Form.Group><Form.Label className="sale-form-label">Deal Name</Form.Label><Form.Control value={dealName} onChange={handleDealNameChange} placeholder="e.g., Azadi-Sale" isInvalid={!!errors.dealName} /><Form.Control.Feedback type="invalid">{errors.dealName}</Form.Control.Feedback></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label className="sale-form-label">URL Slug</Form.Label><Form.Control value={slug} onChange={handleSlugChange} placeholder="auto-generated from name" />{slug && <div className="slug-preview"><FiLink /><span>{process.env.REACT_APP_MARKETPLACE_URL}/sale/<span className="slug-text">{slug}</span></span></div>}</Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label className="sale-form-label"><FiCalendar className="me-1" />Start Date</Form.Label><Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} isInvalid={!!errors.startDate} /><Form.Control.Feedback type="invalid">{errors.startDate}</Form.Control.Feedback></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label className="sale-form-label"><FiCalendar className="me-1" />End Date</Form.Label><Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} isInvalid={!!errors.endDate} /><Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label className="sale-form-label"><FiPercent className="me-1" />Discount %</Form.Label><Form.Control type="number" max={100} value={discountPercentage} onChange={(e) => { const v = e.target.value; if (v === "" || Number(v) <= 100) setDiscountPercentage(v); }} isInvalid={!!errors.discountPercentage} /><Form.Control.Feedback type="invalid">{errors.discountPercentage}</Form.Control.Feedback></Form.Group></Col>
          </Row>
          <div className="sale-divider" />
          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="sale-form-label">Color Gradient</Form.Label>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Form.Control value={colorGradient} onChange={(e) => setColorGradient(e.target.value)} placeholder="linear-gradient(135deg, #a8e063, #56ab2f)" style={{ flex: 1 }} />
                {colorGradient && <div className="gradient-preview" style={{ background: colorGradient }} />}
              </div>
              {errors.colorGradient && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.colorGradient}</div>}
            </Col>
            <Col md={6}>
              <Form.Label className="sale-form-label">Preset Palettes</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {colorPalette.map((p, i) => (
                  <div key={i} className={`gradient-swatch ${colorGradient === `linear-gradient(135deg, ${p.colors[0]}, ${p.colors[1]})` ? "selected" : ""}`} style={{ background: `linear-gradient(135deg, ${p.colors[0]}, ${p.colors[1]})` }} onClick={() => handleColorPaletteSelect(p.colors)} title={p.name} />
                ))}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Section 2: Select Items */}
      <Card className="border-0 shadow-sm mb-3 sale-card">
        <Card.Body className="p-4">
          {renderCardTitle(<FiPackage />, "Select Items", selectedItems.length)}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Form.Label className="sale-form-label">Search by Name</Form.Label>
              <div className="position-relative" ref={dropdownRef}>
                <InputGroup>
                  <InputGroup.Text><FiSearch /></InputGroup.Text>
                  <Form.Control placeholder="Search by name..." value={searchQuery} onChange={handleSearchChange} onFocus={() => setShowDropdown(true)} />
                </InputGroup>
                {isSearching && <span className="spinner-border spinner-border-sm position-absolute" style={{ top: "12px", right: "12px" }} />}
                {renderSearchDropdown(searchResults, isSearching, showDropdown, searchQuery, handleSelectItem)}
              </div>
            </Col>
            <Col md={6}>
              <Form.Label className="sale-form-label">Search by Listing ID</Form.Label>
              <div className="position-relative" ref={dropdownIdRef}>
                <InputGroup>
                  <InputGroup.Text><FiSearch /></InputGroup.Text>
                  <Form.Control type="number" min="1" placeholder="Search by listing ID..." value={searchIdQuery} onChange={handleSearchIdChange} onFocus={() => setShowIdDropdown(true)} />
                </InputGroup>
                {isSearchingId && <span className="spinner-border spinner-border-sm position-absolute" style={{ top: "12px", right: "12px" }} />}
                {renderSearchDropdown(searchIdResults, isSearchingId, showIdDropdown, searchIdQuery, handleSelectItem)}
              </div>
            </Col>
          </Row>
          {errors.selectedItems && <div className="text-danger mb-2" style={{ fontSize: "0.8rem" }}>{errors.selectedItems}</div>}
          {selectedItems.length > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <small className="text-muted">{selectedItems.length} item{selectedItems.length !== 1 ? "s" : ""} selected</small>
              <Button variant="outline-danger" size="sm" onClick={() => setSelectedItems([])}><FiTrash className="me-1" />Remove All</Button>
            </div>
          )}
          {selectedItems.length > 0 ? (
            <div className="selected-items-grid">
              {selectedItems.map((item) => (
                <div key={item.value} className="selected-item-card">
                  {item.image ? (
                    <img src={`${process.env.REACT_APP_URL_S3}/${item.image}`} alt={item.label} className="selected-item-img" />
                  ) : (
                    <div className="selected-item-img-placeholder"><FiImage /></div>
                  )}
                  <div className="selected-item-info">
                    <div className="selected-item-title">{item.label}</div>
                    <div className="selected-item-meta">
                      <small className="text-muted">ID: {item.value}</small>
                      {item.price && <small className="fw-bold">Rs. {Number(item.price).toLocaleString()}</small>}
                    </div>
                  </div>
                  <button type="button" className="selected-item-remove" onClick={() => handleRemoveItem(item)}><FiX /></button>
                </div>
              ))}
            </div>
          ) : (
            <div className="sale-empty-hint">Search and select items to include in this deal</div>
          )}
        </Card.Body>
      </Card>

      {/* Section 3: Banners */}
      <Card className="border-0 shadow-sm mb-3 sale-card">
        <Card.Body className="p-4">
          {renderCardTitle(<FiImage />, "Banners", totalBannerCount)}
          <ImageUploader selectedFiles={banners} setSelectedFiles={setBanners} numberOfImages={5} ref={imageUploaderRef} />
          <div className="mt-3">
            <div className="banner-thumbnail-grid">
              {existingBanners.map((banner, index) => (
                <div key={`existing-${banner.banner_id || index}`} className="banner-thumbnail">
                  <img src={`${process.env.REACT_APP_URL_S3}/${banner.banner_key}`} alt={`Banner ${index}`} />
                  <button type="button" className="remove-btn" onClick={() => onDeleteExistingBanner(banner)}><FiX /></button>
                </div>
              ))}
              {imageLinks.files.map((banner, index) => (
                <div key={`new-${index}`} className="banner-thumbnail">
                  <img src={`${process.env.REACT_APP_URL_S3}/${banner}`} alt={`Banner ${index}`} />
                  <button type="button" className="remove-btn" onClick={() => onDeleteNewBanner(banner)}><FiX /></button>
                </div>
              ))}
            </div>
            {totalBannerCount === 0 && <div className="sale-empty-hint">Upload banner images above</div>}
          </div>
          {errors.banners && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.banners}</div>}
        </Card.Body>
      </Card>

      {/* Section 4: SEO */}
      <Card className="border-0 shadow-sm mb-3 sale-card">
        <Card.Body className="p-4">
          {renderCardTitle(<FiTag />, "SEO Metadata")}
          <Row className="g-3">
            <Col md={6}><Form.Group><Form.Label className="sale-form-label">Meta Title</Form.Label><Form.Control value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} /></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label className="sale-form-label">Meta Keywords</Form.Label><Form.Control value={metaKeywords} onChange={(e) => setMetaKeywords(e.target.value)} /></Form.Group></Col>
            <Col md={12}><Form.Group><Form.Label className="sale-form-label">Meta Description</Form.Label><Form.Control as="textarea" rows={2} value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} /></Form.Group></Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Section 5: Review & Submit */}
      <Card className="border-0 shadow-sm mb-3 sale-card">
        <Card.Body className="p-4">
          {renderCardTitle(<FiCheckCircle />, "Review & Submit")}
          <Row className="g-3">
            <Col md={6}>
              <div className="review-summary">
                <div className="review-summary-row"><span className="review-summary-label"><FiSettings className="me-1" />Deal Name</span><span className="review-summary-value">{dealName || "—"}</span></div>
                <div className="review-summary-row"><span className="review-summary-label"><FiLink className="me-1" />Slug</span><span className="review-summary-value">{slug || "—"}</span></div>
                <div className="review-summary-row"><span className="review-summary-label"><FiPercent className="me-1" />Discount</span><span className="review-summary-value">{discountPercentage ? `${discountPercentage}%` : "—"}</span></div>
              </div>
            </Col>
            <Col md={6}>
              <div className="review-summary">
                <div className="review-summary-row"><span className="review-summary-label"><FiCalendar className="me-1" />Duration</span><span className="review-summary-value">{startDate || "—"} → {endDate || "—"}</span></div>
                <div className="review-summary-row"><span className="review-summary-label"><FiImage className="me-1" />Banners</span><span className="review-summary-value">{totalBannerCount}</span></div>
                <div className="review-summary-row"><span className="review-summary-label"><FiPackage className="me-1" />Items</span><span className="review-summary-value">{selectedItems.length}</span></div>
              </div>
            </Col>
          </Row>
          <div className="sale-submit-bar">
            <Button variant="light" className="border" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} className="sale-submit-btn">
              {isSubmitting ? <><span className="spinner-border spinner-border-sm" /> Loading...</> : editingDeal ? "Update Deal" : "Create Deal"}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </Form>
    </>
  );
}

export default SalesAndDealsForm;
